import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Boxes,
  CalendarCheck,
  Droplet,
  Images,
  LineChart,
  Scale,
  Scissors,
  Star,
  Wind,
} from "lucide-react";

export const hairCarePrompts: CategoryPrompt[] = [
  {
    icon: <Scissors />,
    title: "Create Hair Type Personalization Quizzes",
    description:
      "Build a quiz diagnosing hair type and concerns, then recommend specific product routines with application tutorials.",
    prompt: `**Build a hair type personalization quiz that diagnoses specific hair needs and recommends customized product routines with application guidance.**

**QUIZ FOCUS:** e.g., "Find Your Perfect Hair Routine" OR "What Does Your Hair Actually Need?" OR "Your Custom Hair Care Match"

**PRODUCT RANGE:** e.g., Shampoo, conditioner, treatments, styling (10-25 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] Frustrated with results (current routine not working)
- [ ] New to brand (need guidance on what to buy)
- [ ] Hair goal seekers (growth, repair, volume, etc.)

**Tone & Style:**
- Expert hair stylist advisor
- Empathetic to hair struggles
- Personalized and specific
- Confidence-building

**Quiz Question Guidelines:** Each question should:
- Use hair-specific terminology
- Include visual hair type/texture examples
- Build toward product recommendations
- Address both type and concerns
- 6-8 total questions maximum

**Email Gate Strategy:**
- Place after question #5 (mid-commitment)
- Frame as "Get your personalized hair routine"
- Offer incentive: "Unlock routine + 20% off starter set"
- Single field: email only
- Trust line: "Join 200,000+ with healthier hair"

**Results Page Elements:**
- **Personalized Headline:** "Your hair type: [Type + Concern Profile]"
- **Hair Profile Summary:** "You have [texture] hair with [concern] - here's your routine"
- **Complete Routine:** 3-6 products in order of application
- **Product Details Per Step:** What it does for your specific hair type
- **Application Tutorial:** Video or step-by-step for each product
- **Match Reasoning:** "You said [answer], so this [ingredient] targets that because..."
- **Routine Bundle:** Complete set with bundle discount
- **Alternative Options:** Different products if budget/preference varies
- **Product Cards Include:** Image, hair benefit, key ingredients, application tips, "Add to Routine"
- **Before/After Examples:** Similar hair types showing results
- **Frequency Guide:** How often to use each product
- **Expected Timeline:** "See results in 4-6 weeks"
- **CTAs:** "Start Your Routine" and "Get Complete Set"
- **Retake Option:** "Hair changed? Retake quiz"

**Conversion Tactics Use:**
- Hair type validation ("You're not alone, 40% have this")
- Ingredient targeting for specific concerns
- Routine simplification (not overwhelming)
- Bundle savings on complete sets
- Application education (show proper use)
- Progress timeline expectations

**Key Quiz Questions to Include:**
- **Hair Texture:** Straight, wavy, curly, coily (with visual examples)
- **Hair Thickness:** Fine, medium, thick strands
- **Scalp Type:** Oily, dry, normal, sensitive, dandruff-prone
- **Primary Concern:** Damage, frizz, dryness, oiliness, thinning, color-treated
- **Secondary Issues:** Split ends, breakage, lack of volume, dullness
- **Current Routine:** Over-washing, heat styling frequency, chemical treatments
- **Hair Goals:** Growth, repair, volume, shine, manageability
- **Budget Range:** Essential products only, complete routine, premium care

**Goal:** Convert overwhelmed hair care shoppers into confident buyers by providing personalized routines based on specific hair type and concerns, with clear application guidance and bundled sets that simplify purchasing and increase order value.`,
  },
  {
    icon: <Wind />,
    title: "Hair Problem Listicle Advertorials",
    description:
      "Build a listicle addressing common hair problems, with each issue linking to my solution with science explanations.",
    prompt: `**Build a hair problem listicle that identifies common issues and positions your products as scientifically-backed solutions.**

**TOPIC:** e.g., "7 Reasons Your Hair Won't Grow (And How to Fix It)" OR "5 Hair Mistakes Causing Your Frizz" OR "Why Your Hair Is Breaking—And What Actually Works"

**PRODUCTS FEATURED:** e.g., "Our bond repair treatment solves #3" OR "Our scalp serum fixes issue #5"

**Reader Type:** Choose one:
- [ ] Problem-frustrated (dealing with persistent issue)
- [ ] Solution-searching (tried everything, nothing works)
- [ ] Knowledge-seeking (want to understand why)

**Tone & Style:**
- Expert trichologist knowledge
- Empathetic to hair struggles
- Science-backed not marketing fluff
- Empowering through education

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("Fix Breakage Now")
- Use ingredient science ("Powered by [Active]")
- Include proof element ("Clinically Tested")
- Emphasize results ("See Results in 4 Weeks")

**Conversion Tactics Use:**
- Scientific explanations for problems
- Ingredient targeting for solutions
- Before/after photos from similar issues
- Clinical study references
- Hair expert validation quotes
- Treatment timelines and expectations

**Key Elements to Include:**
- **Hook:** Relatable hair frustration moment (2-3 paragraphs)
- **List Items (6-9 problems):** Numbered issue → science of why → your solution
- **Problem Structure Per Item:**
  - Issue headline: "Problem #X: [Specific hair issue]"
  - Why this happens: Scientific explanation of root cause
  - Common mistakes making it worse
  - The solution: Your product with active ingredients
  - How it works: Ingredient science breakdown
  - Application guidance: How to use for this issue
  - Expected timeline: When to see improvement
  - Customer testimonial: Someone who solved this exact problem
- **Product Callouts:** Active ingredient highlights with "Shop Solution" CTA
- **Ingredient Spotlights:** Deep dives on key actives (keratin, biotin, peptides) (2-3 throughout)
- **Science Backing:** Clinical studies or dermatologist quotes
- **Before/After Gallery:** Real results for each problem type
- **Routine Integration:** How product fits into hair care routine
- **Multi-Problem Bundles:** If customer has multiple issues
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Safe for color-treated? Daily use? Works for all hair types? Ingredient allergies?)

**Goal:** Convert frustrated hair care shoppers into loyal customers by educating them on the science behind their problems and demonstrating how your products' specific active ingredients provide targeted, proven solutions they can trust.`,
  },
  {
    icon: <Boxes />,
    title: "Results-Focused Protocol Pages",
    description:
      "Create a results-focused page with commitment positioning, progress tracking guide, and money-back guarantee.",
    prompt: `**Build a results-focused protocol page that positions your product as a commitment program with trackable progress and guaranteed outcomes.**

**PROTOCOL FOCUS:** e.g., "90-Day Hair Growth Protocol" OR "8-Week Damage Repair Challenge" OR "30-Day Volume Transformation"

**RESULT PROMISE:** e.g., "Visible growth in 90 days" OR "Stronger hair in 8 weeks or money back"

**Target User:** Choose one:
- [ ] Results-desperate (willing to commit for real change)
- [ ] Protocol followers (like structured programs)
- [ ] Skeptics needing proof (guarantee-motivated)

**Tone & Style:**
- Commitment and discipline-focused
- Clinical program framing
- Evidence-based confidence
- Coaching and accountability energy

**CTA Guidelines:** Each CTA should:
- Use commitment language ("Start Your Protocol")
- Reference timeline ("Begin 90 Days")
- Include guarantee ("Results or Refund")
- Emphasize transformation ("Transform Your Hair")

**Conversion Tactics Use:**
- Week-by-week progress milestones
- Photo documentation requirements
- Money-back results guarantee
- Progress tracking tools provided
- Before/after comparison galleries
- Accountability check-ins or reminders

**Key Elements to Include:**
- **Hero Section:** Protocol headline, timeline (e.g., 90 days), result promise, "Start Protocol" CTA
- **The Commitment:** What this protocol requires (consistency, photo tracking, full routine)
- **Results Guarantee:** "Visible results in X weeks or 100% money back"
- **Week-by-Week Protocol:**
  - Week 1-2: What to expect (adjustment period)
  - Week 3-4: First changes (texture, strength)
  - Week 6-8: Visible improvement (growth, volume, repair)
  - Week 10-12: Transformation results (final outcome)
- **Required Products:** Complete protocol kit with application schedule
- **Daily Routine Breakdown:** Morning and evening steps with timing
- **Progress Tracking Guide:** How to document with photos (lighting, angles, frequency)
- **Milestone Checklist:** What to look for at each phase
- **Before/After Gallery (8-12):** Real protocol followers with timelines
- **Success Stories:** Detailed testimonials from protocol completers
- **Scientific Backing:** Clinical studies or ingredient efficacy data
- **Progress Tracking Tools:** Downloadable photo tracker or app integration
- **Common Obstacles:** What to expect and how to push through
- **Support Resources:** Community, email check-ins, troubleshooting
- **Money-Back Details:** Exact guarantee terms and claim process
- **Protocol Bundle:** All products at discount vs. individual
- **CTAs:** Hero, after guarantee, with bundle, after gallery (5-6 total)
- **FAQ:** 6-8 questions (Miss a day? Results timeline? Guarantee proof needed? Works for my hair type? Cost?)

**Goal:** Convert commitment-ready shoppers into loyal customers through structured protocol framing that sets clear expectations, provides accountability tools, and removes risk with results guarantee—building trust through transparency and program discipline.`,
  },
  {
    icon: <LineChart />,
    title: "Transformation Photo Galleries",
    description:
      "Create a UGC gallery where customers submit before/after photos tagged with hair type and concern, with filtering.",
    prompt: `**Build a transformation photo gallery showcasing real customer before/after results organized by hair type and concern for relatable social proof.**

**GALLERY FOCUS:** e.g., "Real Results, Real People" OR "Customer Hair Transformations" OR "See the Difference"

**FILTER CATEGORIES:** e.g., By hair type, by concern, by product used, by timeline

**Gallery Visitor Type:** Choose one:
- [ ] Results-skeptics (need proof it works)
- [ ] Similar-hair seekers (want to see their type)
- [ ] Timeline-curious (how long until results)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of real progress
- Empathetic to hair struggles
- Results-focused not perfection-focused

**CTA Guidelines:** Each CTA should:
- Use transformation language ("Get Your Results")
- Reference similar success ("Works for Your Hair Type")
- Include timeline context ("See Change in 6 Weeks")
- Link to concern-specific products

**Conversion Tactics Use:**
- Filter by exact hair type/concern match
- Timeline tags (4 weeks, 8 weeks, 12 weeks)
- Product routine used displayed
- Verified purchase badges
- Photo submission incentive (discount)
- Progress photo series (multiple timepoints)

**Key Elements to Include:**
- **Hero Section:** Headline, before/after grid preview, filter options, "Submit Your Photos" CTA
- **Filter Options:** By hair type (straight/wavy/curly/coily), by concern (growth/damage/frizz/thinning), by timeline, by product, verified only
- **Before/After Grid:** Side-by-side images with hover details (name, hair type, concern, timeline, products)
- **Transformation Stories (4-6 featured):** Large B/A images, customer name/hair details, full journey (2-3 paragraphs), timeline, routine used, "Shop Her Routine" CTA
- **Timeline Breakdown:** Results at weeks 2, 4, 8, 12 with progress photos
- **Concern-Specific Galleries:** Dedicated sections per issue (damage, growth, volume)
- **Product Routines Displayed:** Which products each person used for results
- **Transformation Metrics:** "8,500+ real transformations" or "Average visible change in 6 weeks"
- **Hair Type Labels:** 2A, 3C, 4B classification tags for relatability
- **Progress Series:** Multi-photo timelines showing gradual change
- **Photo Submission Section:** "Share your B/A & get 30% off" with upload
- **Authenticity Indicators:** Verified purchase, unedited photo badges, same lighting requirement
- **CTAs:** Top of page, with featured stories, concern galleries (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Photo requirements? Privacy? Incentive details?)

**Goal:** Convert skeptical shoppers into confident buyers by showcasing authentic, concern-specific transformations with realistic timelines that prove your products deliver real results for people with similar hair types and issues.`,
  },
  {
    icon: <Images />,
    title: "Hair Science Diagnostic Quizzes",
    description:
      "Build a quiz helping customers diagnose hair needs, explaining symptoms and recommending products based on diagnosis.",
    prompt: `**Build a hair science diagnostic quiz that helps customers identify root causes of hair issues and recommends targeted product solutions based on diagnosis.**

**QUIZ FOCUS:** e.g., "What's Really Wrong With Your Hair?" OR "Diagnose Your Hair Health" OR "Find Your Hair's Hidden Problem"

**DIAGNOSTIC RANGE:** e.g., Damage, dryness, scalp issues, thinning, breakage (8-15 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] Problem-aware (know symptoms, need cause)
- [ ] Frustrated fixers (treatments not working)
- [ ] Knowledge-seekers (want to understand their hair)

**Tone & Style:**
- Clinical diagnostic approach
- Educational expert voice
- Empowering through understanding
- Solution-oriented not alarming

**Quiz Question Guidelines:** Each question should:
- Focus on symptoms not just preferences
- Use diagnostic terminology
- Include visual examples of conditions
- Build toward root cause identification
- 7-9 total questions maximum

**Email Gate Strategy:**
- Place after question #5 (deep commitment)
- Frame as "Get your personalized hair diagnosis"
- Offer incentive: "Unlock diagnosis + treatment plan + 25% off"
- Single field: email only
- Trust line: "Join 150,000+ who discovered their hair needs"

**Results Page Elements:**
- **Diagnosis Headline:** "Your diagnosis: [Primary Issue + Contributing Factors]"
- **Diagnosis Explanation:** What's causing symptoms (science breakdown, 2-3 paragraphs)
- **Symptom Analysis:** Which symptoms pointed to this diagnosis
- **Root Cause:** Why this is happening (lifestyle, genetics, damage, etc.)
- **Treatment Protocol:** 3-5 products targeting diagnosed issues in order
- **Product Details Per Step:** How each treats specific aspect of diagnosis
- **Ingredient Targeting:** Which actives address root cause
- **Application Guidance:** Treatment frequency and technique
- **Expected Timeline:** "Address symptoms in 2 weeks, repair in 8-12 weeks"
- **Lifestyle Recommendations:** Habits to change alongside products
- **Prevention Tips:** Avoid worsening diagnosis
- **Product Cards Include:** Image, diagnostic benefit, key ingredients, usage instructions
- **Match Reasoning:** "Your symptoms suggest [issue], so this [ingredient] repairs..."
- **Alternative Diagnoses:** If symptoms could indicate multiple issues
- **CTAs:** "Start Treatment" and "Get Complete Protocol"
- **Retake Option:** "Symptoms changed? Retake diagnostic"

**Conversion Tactics Use:**
- Medical-style diagnostic framing
- Root cause education (not just symptoms)
- Multi-symptom addressing
- Treatment protocol vs. single product
- Science-backed ingredient selection
- Progress monitoring guidance

**Key Quiz Questions to Include:**
- **Primary Symptom:** Breakage, shedding, dryness, oiliness, thinning, frizz
- **Texture Changes:** When started, progression, severity
- **Scalp Condition:** Itchy, flaky, oily, dry, sensitive, inflamed
- **Breakage Location:** Mid-shaft, ends, roots (diagnostic indicator)
- **Elasticity Test:** Does hair snap or stretch when wet
- **Shedding Pattern:** Amount, frequency, when occurs
- **Lifestyle Factors:** Heat styling, chemical treatments, diet, stress
- **Previous Treatments:** What's been tried, what failed
- **Timeline:** How long experiencing symptoms

**Goal:** Convert confused hair care shoppers into committed treatment buyers by providing clinical-style diagnosis that explains the root cause of their issues, then recommending targeted product protocols that address symptoms scientifically rather than guessing with random products.`,
  },
  {
    icon: <CalendarCheck />,
    title: "Ingredient Problem Advertorials",
    description:
      "Build an advertorial explaining common ingredient problems, comparing alternatives, and positioning your formula as the solution.",
    prompt: `**Build an ingredient-focused advertorial that exposes harmful common ingredients and positions your clean formulations as the superior, safe alternative.**

**TOPIC:** e.g., "5 Toxic Ingredients Secretly Damaging Your Hair" OR "Why Sulfates Are Ruining Your Curls" OR "The Hidden Chemicals Making Your Hair Worse"

**YOUR FORMULA:** e.g., "Our sulfate-free, silicone-free formulas" OR "Clean hair care that works"

**Reader Type:** Choose one:
- [ ] Ingredient-conscious (read labels, avoid toxins)
- [ ] Damage-frustrated (current products making it worse)
- [ ] Clean beauty converts (seeking trustworthy alternatives)

**Tone & Style:**
- Educational exposé energy
- Science-backed not fear-mongering
- Empowering through transparency
- Solution-focused ending

**CTA Guidelines:** Each CTA should:
- Reference clean formulation ("Shop Toxin-Free")
- Use safety language ("Safe for Daily Use")
- Include ingredient proof ("See Full Ingredients")
- Emphasize effectiveness ("Clean AND Effective")

**Conversion Tactics Use:**
- Harmful ingredient exposure with science
- Your clean alternative ingredients explained
- Side-by-side formulation comparisons
- Before/after from switching
- Dermatologist validation
- Full ingredient transparency

**Key Elements to Include:**
- **Hook:** Reveal surprising harmful ingredient (2-3 paragraphs)
- **List Items (5-8 ingredients):** Numbered harmful ingredient → why it's bad → your clean alternative
- **Ingredient Structure Per Item:**
  - Harmful ingredient headline: "#X: [Chemical name] (Found in 70% of shampoos)"
  - What it does: Purpose in traditional formulas
  - The problem: Health/hair damage with scientific backing
  - Long-term effects: Cumulative damage explained
  - Your alternative: Clean ingredient that works better
  - How yours works: Science of your safe ingredient
  - Efficacy proof: Studies or results data
  - Customer testimonial: Someone who switched and saw improvement
- **Product Callouts:** Clean formulation highlights with "Shop Clean" CTA
- **Comparison Chart:** Toxic conventional vs. your clean formula (4-6 rows)
- **Full Ingredient Disclosure:** Your complete formula with each ingredient explained
- **Scientific Backing:** Studies on harmful ingredients and safe alternatives
- **Dermatologist Quote:** Expert validation of clean approach
- **Certification Badges:** EWG Verified, dermatologist-tested, etc.
- **What's NOT In Yours:** Comprehensive exclusion list (sulfates, parabens, etc.)
- **Efficacy Proof:** Clean doesn't mean less effective - show results
- **CTAs:** After ingredient #2, after ingredient #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Really as effective? Safe for color-treated? All natural? Shelf life? Cost more?)

**Goal:** Convert ingredient-conscious shoppers into loyal customers by exposing harmful chemicals in conventional hair care and proving your clean formulations deliver equal or superior results without compromising hair health or long-term safety.`,
  },
  {
    icon: <Scale />,
    title: "Influencer Routine Collaboration Pages",
    description:
      "Create an influencer collaboration page featuring their video tutorial, product usage tips, and exclusive code.",
    prompt: `**Build an influencer routine collaboration page that leverages creator credibility through video tutorials, expert tips, and exclusive discount codes.**

**INFLUENCER/CREATOR:** e.g., "@curlyhairjessica" OR "Mane Addicts by Alex" OR "Hair by Chrissy"

**COLLABORATION TYPE:** e.g., "Signature routine" OR "Curated hair essentials" OR "Expert partnership"

**Audience Type:** Choose one:
- [ ] Influencer's followers (trust their expertise)
- [ ] Hair goal seekers (want their results)
- [ ] Tutorial learners (need application guidance)

**Tone & Style:**
- Creator's authentic voice
- Educational and inspiring
- Community insider feel
- Exclusive access energy

**CTA Guidelines:** Each CTA should:
- Reference creator ("Shop Jessica's Routine")
- Use exclusive language ("Get Her Exact Products")
- Include discount code prominently
- Emphasize results ("Get Her Curls")

**Conversion Tactics Use:**
- Exclusive creator discount code
- Full routine video tutorial
- Step-by-step application tips
- Creator's before/after results
- Limited-edition collaboration items
- Bundle creator's complete routine

**Key Elements to Include:**
- **Hero Section:** Creator photo/video, collaboration headline, exclusive code display, primary CTA
- **About This Collab:** Creator intro + why they chose these products (2-3 paragraphs)
- **Exclusive Discount Code:** Large, copy-able code with savings % and expiration
- **Full Routine Video:** 3-5 min tutorial showing complete application process
- **Creator's Routine Breakdown:** Step-by-step with timing and techniques
- **Product Carousel:** 4-8 featured products with creator's specific usage tips per item
- **Creator Quote Pulls:** Personal insights on each product as visual breaks
- **Before/After:** Creator's hair transformation using routine
- **Application Tips:** Pro techniques creator uses for best results
- **Why She Chose Each:** Creator explanations for product selection
- **Complete Routine Bundle:** "Get Jessica's Exact Routine" with bundle discount
- **Hair Type Context:** Creator's hair specs (type, texture, concerns)
- **Frequency Guide:** How often creator uses routine
- **Troubleshooting:** Creator's answers to common questions
- **Community Content:** Follower results recreating routine
- **Limited-Time Element:** If applicable, countdown to collab end
- **CTAs:** Hero, after video, with each product, bundle section (5-7 total)
- **FAQ:** 4-5 questions (Code expiration? Works for other hair types? Products sold separately? Creator earnings disclosure?)

**Goal:** Convert influencer's engaged audience into customers by leveraging authentic creator expertise, providing educational application content, and offering exclusive discount codes that make followers feel part of an insider hair care community.`,
  },
  {
    icon: <Droplet />,
    title: "Professional Endorsement Pages",
    description:
      "Build a page featuring professional endorsements, salon testimonials, and pro tip content from licensed stylists.",
    prompt: `**Build a professional endorsement page that leverages stylist credibility and salon expertise to position your products as professional-grade solutions.**

**ENDORSEMENT FOCUS:** e.g., "Trusted by Professional Stylists" OR "Salon-Quality for Home Use" OR "What Hairstylists Actually Recommend"

**PROFESSIONAL TYPES:** e.g., Licensed stylists, colorists, trichologists, salon owners

**Target Audience:** Choose one:
- [ ] Quality-seekers (want professional results)
- [ ] Salon-product believers (trust stylist recommendations)
- [ ] DIY-hesitant (need expert validation)

**Tone & Style:**
- Credible and authoritative
- Professional expertise-focused
- Behind-the-salon-chair insights
- Accessible pro knowledge

**CTA Guidelines:** Each CTA should:
- Reference professional approval ("Stylist-Recommended")
- Use quality language ("Salon-Grade Results")
- Include credibility ("Licensed Professional Trusted")
- Emphasize expertise ("Pro-Tested")

**Conversion Tactics Use:**
- Multiple stylist endorsements (not just one)
- Salon testimonials with location/name
- Professional credentials displayed
- Pro technique tutorials
- Side-by-side salon vs. home results
- Years of experience credibility

**Key Elements to Include:**
- **Hero Section:** "Trusted by 5,000+ Professional Stylists" headline, stylist collage, "Shop Pro-Approved" CTA
- **Professional Advisory Panel:** 4-6 featured stylists with photos, full credentials, salon affiliations
- **Stylist Endorsements (5-8):**
  - Stylist headshot + name
  - Credentials (licensed, years experience, specialties)
  - Salon name + location
  - "Why I Recommend" statement (100-150 words)
  - Products they use on clients
  - Client result examples
  - Video testimonial if available
- **Salon Partnerships:** Named salons using your products professionally
- **Pro Tips Section:** Expert techniques from stylists for at-home use
- **Product-Specific Pro Quotes:** Each product gets stylist endorsement
- **Professional Technique Videos:** 2-3 tutorials showing proper application
- **Salon vs. Home Results:** Comparable outcomes with your products
- **Credentials Display:** License numbers, certifications, education
- **Pro Product Comparison:** Why stylists choose yours over salon backbar brands
- **Client Testimonials:** "My stylist recommended this" reviews
- **Industry Recognition:** Awards, features in trade publications
- **Wholesale/Pro Program:** Option for stylists to purchase
- **CTAs:** Hero, after endorsements, with products, pro program (5-6 total)
- **FAQ:** 4-5 questions (Really professional-grade? Available in salons? Pro discount? Training needed?)

**Goal:** Convert quality-conscious consumers into confident buyers by providing overwhelming professional validation that proves your products meet salon standards, backed by licensed stylist expertise and credentials that build unshakeable trust in product efficacy.`,
  },
  {
    icon: <Star />,
    title: "Subscription Landing Pages",
    description:
      "Create a subscription page synced to product use cycle, emphasizing consistency for results and featuring subscriber-only products.",
    prompt: `**Build a subscription landing page that converts one-time buyers into recurring customers by syncing deliveries to product usage cycles and emphasizing consistency for hair results.**

**SUBSCRIPTION OFFER:** e.g., "Monthly Hair Care Delivery" OR "Routine Replenishment Program" OR "Never Run Out Subscription"

**VALUE PROPOSITION:** e.g., "Save 25% + consistent results" OR "Synced to your usage + subscriber exclusives"

**Subscription Model:** Choose one:
- [ ] Usage-synced (delivers when product runs out)
- [ ] Routine-based (complete regimen monthly)
- [ ] Customizable (adjust products and frequency)

**Tone & Style:**
- Consistency and commitment-focused
- Results require routine emphasis
- Convenience and automation
- "Hair health is a journey" framing

**CTA Guidelines:** Each CTA should:
- Emphasize consistency ("Commit to Results")
- Use savings language ("Save 25% Every Order")
- Reference convenience ("Never Run Out")
- Include flexibility ("Pause Anytime")

**Conversion Tactics Use:**
- Usage cycle calculator (when you'll run out)
- Results timeline tied to consistency
- Subscriber-only exclusive products
- First subscription discount
- Savings calculator (annual total)
- Easy skip/pause/cancel demonstration

**Key Elements to Include:**
- **Hero Section:** Subscription headline, savings %, "Start Subscription" CTA
- **Why Consistency Matters:** Results require regular use - show timeline without gaps
- **Value Proposition:** 3-4 benefits (save 25%, never run out, exclusive access, priority shipping)
- **Usage Cycle Calculator:** "Your routine lasts X weeks, we deliver every Y weeks"
- **Pricing Display:**
  - Per delivery price vs. one-time
  - Savings per order: "Save $X every shipment"
  - Annual savings: "Save $XXX/year"
- **How It Works:** 3 steps (Choose products → Set frequency → Auto-delivered)
- **Customization Options:**
  - Delivery frequency (every 4/6/8/10 weeks based on usage)
  - Product selection (full routine or individual items)
  - Quantity adjustments
- **Results Timeline:** "Consistency = Results" visual showing 4/8/12 week progress with subscription
- **Subscriber-Only Perks:**
  - 25% off all subscription items
  - Exclusive limited-edition products
  - Early access to new launches
  - Free shipping always
  - Priority customer support
- **Exclusive Products Section:** Items only available to subscribers
- **Flexibility Features:** Skip, pause, cancel badges with "modify anytime" messaging
- **Subscriber Testimonials:** Focus on convenience and results from consistency
- **Social Proof:** "120,000+ subscribers with healthier hair"
- **First Box Incentive:** "50% off first delivery + bonus travel size"
- **CTAs:** Hero, after usage calculator, after perks, footer (5-6 total)
- **FAQ:** 7-9 questions (Cancel how? Skip shipments? Change products? Adjust frequency? Subscriber exclusives? Pause? Gift subscriptions?)

**Goal:** Convert one-time buyers into loyal subscribers by emphasizing that hair results require product consistency, offering significant savings and exclusive perks, while removing commitment anxiety through easy modification and cancellation options synced to actual product usage cycles.`,
  },
];
